<template>
  <div>
    <button
      :id="`${line.key}-header`"
      type="button"
      class="flex w-full items-center justify-between px-4 py-4 text-left hover:bg-gray-50 md:px-6"
      :aria-expanded="expanded"
      :aria-controls="`${line.key}-content`"
      @click="expanded = !expanded"
    >
      <span class="flex min-w-0 flex-1 items-center gap-3">
        <BaseIcon
          :name="expanded ? 'arrow-down-s' : 'arrow-right-s'"
          class="shrink-0 text-lg text-gray-400"
          aria-hidden="true"
        />
        <span class="min-w-0">
          <span class="flex flex-wrap items-center gap-2">
            <span class="font-medium text-gray-900">{{ line.label }}</span>
            <BaseBadge
              v-if="!isDefinitive && line.isProvisional"
              class="bg-warning/10 text-warning text-xs"
            >
              Voorlopig
            </BaseBadge>
            <BaseBadge
              v-if="line.noData"
              class="bg-gray-100 text-xs text-gray-400"
            >
              Data niet beschikbaar
            </BaseBadge>
          </span>
          <span v-if="line.subtitle" class="mt-0.5 block text-xs text-gray-500">
            {{ line.subtitle }}
          </span>
        </span>
      </span>
      <span class="ml-4 shrink-0 font-semibold" :class="amountClass">
        {{ formatEuro(line.amount) }}
      </span>
    </button>

    <section
      v-show="expanded"
      :id="`${line.key}-content`"
      :aria-labelledby="`${line.key}-header`"
      class="bg-gray-50"
    >
      <div class="px-8 py-3 md:px-10">
        <p
          class="mb-2 text-xs font-medium tracking-wide text-gray-400 uppercase"
        >
          Zo is dit bedrag berekend
        </p>
        <div v-if="!hasBreakdown" class="text-sm text-gray-400 italic">
          Grondslag niet beschikbaar.
        </div>
        <div
          v-else
          class="space-y-2 rounded-lg border border-gray-200 bg-white p-3 text-sm md:p-4"
        >
          <div
            v-if="breakdownItems.length"
            class="space-y-1 border-b border-gray-300 pb-2"
          >
            <div
              v-for="(item, index) in breakdownItems"
              :key="item.label"
              class="grid grid-cols-[1fr_auto_1.25rem] items-baseline gap-3"
            >
              <span class="text-gray-900">{{ item.label }}</span>
              <span class="text-right font-medium text-gray-900">
                {{ formatBreakdownAmount(item) }}
              </span>
              <span class="text-right text-gray-500">
                {{ index > 0 ? (item.operator ?? "+") : "" }}
              </span>
            </div>
          </div>
          <div
            class="grid grid-cols-[1fr_auto_1.25rem] items-baseline gap-3 font-semibold"
          >
            <span class="text-gray-900">
              {{ line.simpleBreakdown.total.label }}
            </span>
            <span class="text-right text-gray-900">
              {{ formatBreakdownAmount(line.simpleBreakdown.total) }}
            </span>
            <span></span>
          </div>
        </div>
      </div>

      <!-- Detail table -->
      <div class="border-t border-gray-100 px-8 py-3 md:px-10">
        <p
          class="mb-2 text-xs font-medium tracking-wide text-gray-400 uppercase"
        >
          {{ detailHeading }}
        </p>
        <div v-if="showMonthOnlyHint" class="text-sm text-gray-400 italic">
          Deze informatie is alleen op maandniveau beschikbaar.
        </div>
        <div
          v-else-if="granularity === GRANULARITY.QUARTER && quarterFailed"
          class="text-sm text-gray-400 italic"
          role="alert"
        >
          De kwartierdata kon niet worden opgehaald.
        </div>
        <div
          v-else-if="granularity === GRANULARITY.QUARTER && loadingQuarter"
          class="text-sm text-gray-400 italic"
          aria-live="polite"
        >
          Kwartier-data wordt geladen..
        </div>
        <div v-else-if="line.noData" class="text-sm text-gray-400 italic">
          Geen gedetailleerde data beschikbaar.
        </div>
        <div
          v-else-if="!detailRows.length || !hasDetailData"
          class="text-sm text-gray-400 italic"
        >
          Er is helaas geen gedetailleerde data beschikbaar.
        </div>
        <template v-else>
          <div
            class="overflow-x-auto rounded-lg border border-gray-200 bg-white"
          >
            <BaseTable
              :columns="detailColumns"
              :rows="detailTableRows"
              :aria-label="`${line.label} — ${detailHeading}`"
            >
              <template
                v-for="({ isResult }, key) in detailColumns"
                :key="key"
                #[key]="{ header }"
              >
                <span :class="{ 'text-gray-350': !isResult }">{{
                  header
                }}</span>
              </template>
            </BaseTable>
          </div>
          <div v-if="detailRows.length > defaultRowLimit" class="mt-2">
            <button
              type="button"
              class="text-primary text-xs hover:underline"
              :aria-expanded="expandedAllRows"
              @click="expandedAllRows = !expandedAllRows"
            >
              {{ rowToggleLabel }}
            </button>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from "vue"
import {
  BaseBadge,
  BaseIcon,
  BaseTable,
} from "@repowerednl/ui-component-library"
import { GRANULARITY, LINE_GRANULARITY } from "@/helpers/energyBillConstants.js"
import {
  formatBreakdownAmount,
  formatDetailCell,
  formatEuro,
} from "@/helpers/energyBillFormat.js"

const props = defineProps({
  /**
   * A single invoice line to render as an expandable row. Expected shape:
   * - key: unique identifier, used for element ids and the v-for key
   * - label / subtitle: heading and optional sub-heading text
   * - amount: the line total (null when no data is available)
   * - isProvisional: marks the line as not yet definitive (shows a badge)
   * - noData: true when no data is available for this line
   * - granularity: the granularity the line's data is available at (see
   *   LINE_GRANULARITY); a MONTH line has no per-quarter detail
   * - simpleBreakdown: { items: [...], total: {...} } describing how the
   *   amount was calculated
   * - detailedColumns: column definitions for the detail table
   * - dailyRows / quarterRows: the detail rows shown per day or per quarter
   */
  line: { type: Object, required: true },
  /**
   * Whether the month's results are definitive. When false, provisional lines
   * are flagged with a "Voorlopig" badge.
   */
  isDefinitive: { type: Boolean, required: true },
  /**
   * The selected detail granularity (see GRANULARITY). Determines the detail
   * heading ("Per dag" / "Per kwartier") and which rows are shown.
   */
  granularity: { type: String, required: true },
  /** Whether the per-quarter detail data is still being loaded. */
  loadingQuarter: { type: Boolean, default: false },
  /** Whether loading the per-quarter detail data failed. */
  quarterFailed: { type: Boolean, default: false },
  /**
   * Number of detail rows shown before the "Meer rijen tonen" toggle appears.
   */
  defaultRowLimit: { type: Number, default: 5 },
})

const RESULT_FORMATS = "euro"

const expanded = ref(false)
const expandedAllRows = ref(false)

const amountClass = computed(() => {
  const amount = props.line.amount
  if (amount == null) {
    return "text-gray-400"
  }
  return "text-gray-900"
})

const detailHeading = computed(() =>
  props.granularity === GRANULARITY.QUARTER ? "Per kwartier" : "Per dag",
)

const showMonthOnlyHint = computed(
  () =>
    props.granularity === GRANULARITY.QUARTER &&
    props.line.granularity === LINE_GRANULARITY.MONTH &&
    !props.line.noData,
)

const detailRows = computed(() => {
  if (
    props.granularity === GRANULARITY.QUARTER &&
    props.line.granularity !== LINE_GRANULARITY.MONTH
  ) {
    return props.line.quarterRows ?? []
  }
  return props.line.dailyRows ?? []
})

const visibleDetailRows = computed(() => {
  if (expandedAllRows.value) {
    return detailRows.value
  }
  return detailRows.value.slice(0, props.defaultRowLimit)
})

const visibleColumns = computed(() =>
  props.line.detailedColumns.filter((column) =>
    detailRows.value.some((row) => row[column.key] != null),
  ),
)

const hasDetailData = computed(() =>
  visibleColumns.value.some((column) => column.key !== "datum"),
)

const breakdownItems = computed(() =>
  (props.line.simpleBreakdown?.items ?? []).filter(
    (item) => item.amount != null,
  ),
)

const hasBreakdown = computed(
  () => !props.line.noData && props.line.simpleBreakdown?.total,
)

// BaseTable column specs keyed by column key. The "datum" column is relabelled
// "Tijd" when showing per-quarter data, and right-aligned columns carry the
// matching cell class.
const detailColumns = computed(() =>
  Object.fromEntries(
    visibleColumns.value.map((column) => [
      column.key,
      {
        label:
          column.key === "datum" && props.granularity === GRANULARITY.QUARTER
            ? "Tijd"
            : column.label,
        classes: column.align === "right" ? "text-right" : "",
        // Cells are pre-formatted to strings below, so skip BaseTable's own
        // number formatting (which can't do our per-column euro/volume units).
        rawNumbers: true,
        isResult: column.format === RESULT_FORMATS,
      },
    ]),
  ),
)

const detailTableRows = computed(() =>
  visibleDetailRows.value.map((row) =>
    Object.fromEntries(
      visibleColumns.value.map((column) => [
        column.key,
        formatDetailCell(row[column.key], column),
      ]),
    ),
  ),
)

const rowToggleLabel = computed(() => {
  if (expandedAllRows.value) {
    return "Minder rijen tonen"
  }
  const remaining = detailRows.value.length - props.defaultRowLimit
  return `Meer rijen tonen (${remaining} overige)`
})
</script>
